import { makeStyles, Avatar, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider } from '@fluentui/react-components';
import {
  HomeFilled,
  HomeRegular,
  RocketRegular,
  RocketFilled,
  BoxMultipleRegular,
  BoxMultipleFilled,
  DocumentBulletListRegular,
  DocumentBulletListFilled,
  SettingsRegular,
  SettingsFilled,
  SignOutRegular,
  PersonRegular,
  bundleIcon,
} from '@fluentui/react-icons';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LOGO_URL, PRODUCT_NAME } from '../../branding';

const DashboardIcon = bundleIcon(HomeFilled, HomeRegular);
const DeployIcon = bundleIcon(RocketFilled, RocketRegular);
const DeploymentsIcon = bundleIcon(DocumentBulletListFilled, DocumentBulletListRegular);
const ProductsIcon = bundleIcon(BoxMultipleFilled, BoxMultipleRegular);
const SettingsIcon = bundleIcon(SettingsFilled, SettingsRegular);

const useStyles = makeStyles({
  sidebar: {
    width: '260px',
    minWidth: '260px',
    height: '100vh',
    background: 'linear-gradient(180deg, #0b5789 0%, #0f4c75 100%)',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
  },
  // The FirstIQ logo is drawn for light backgrounds (dark-blue "First",
  // charcoal "IQ"), so the brand block sits on white above the rail.
  brand: {
    backgroundColor: 'white',
    padding: '16px 20px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    textDecoration: 'none',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  },
  logo: {
    width: '168px',
    height: 'auto',
    display: 'block',
  },
  appName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#0b5789',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
  },
  nav: {
    flex: 1,
    padding: '16px 12px',
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '8px',
    color: 'rgba(255, 255, 255, 0.85)',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '400',
    transition: 'all 0.15s ease',
    marginBottom: '2px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      textDecoration: 'none',
    },
  },
  navItemActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    fontWeight: '500',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
  },
  navIcon: {
    fontSize: '20px',
    width: '20px',
    height: '20px',
    opacity: 0.8,
  },
  userSection: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  avatar: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: '13px',
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userEmail: {
    fontSize: '11px',
    opacity: 0.7,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
}

function NavItem({ to, icon: Icon, label }: NavItemProps) {
  const styles = useStyles();
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <NavLink
      to={to}
      className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
    >
      <Icon className={styles.navIcon} filled={isActive} />
      <span>{label}</span>
    </NavLink>
  );
}

export function Sidebar() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const handleLogout = () => {
    void instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const initials = account?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <aside className={styles.sidebar}>
      <NavLink to="/deploy" className={styles.brand} title={PRODUCT_NAME}>
        <img className={styles.logo} src={LOGO_URL} alt="FirstIQ" />
        <span className={styles.appName}>Deployment Manager</span>
      </NavLink>

      <nav className={styles.nav} aria-label="Main navigation">
        <NavItem to="/dashboard" icon={DashboardIcon} label="Dashboard" />
        <NavItem to="/deploy" icon={DeployIcon} label="Deploy" />
        <NavItem to="/deployments" icon={DeploymentsIcon} label="Deployments" />
        <NavItem to="/products" icon={ProductsIcon} label="Products" />
        <NavItem to="/settings" icon={SettingsIcon} label="Settings" />
      </nav>

      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <div className={styles.userSection}>
            <Avatar
              name={account?.name || 'Signed in'}
              initials={initials}
              size={36}
              className={styles.avatar}
            />
            <div className={styles.userInfo}>
              <div className={styles.userName}>{account?.name || 'Signed in'}</div>
              {account?.username && (
                <div className={styles.userEmail}>{account.username}</div>
              )}
            </div>
          </div>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<PersonRegular />} disabled>
              {account?.username || 'Profile'}
            </MenuItem>
            <MenuItem icon={<SettingsRegular />} onClick={() => navigate('/settings')}>
              Settings
            </MenuItem>
            <MenuDivider />
            <MenuItem icon={<SignOutRegular />} onClick={handleLogout}>
              Sign out
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </aside>
  );
}
